import { App, type URLOpenListenerEvent } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

/**
 * Normalise a deep link URL to a router-compatible path.
 *
 * Strips the custom URL scheme, preserving the full pathname and search string
 * so that deep links like `clipboardworks.workplace:///login/verify?authUrl=...`
 * are passed through to the router as `/login/verify?authUrl=...`.
 */
function normaliseDeepLink(rawUrl: string): string {
  const urlObject = new URL(rawUrl);
  return `${urlObject.pathname}${urlObject.search}` || "/";
}

/**
 * Initialize deep link listener for Capacitor mobile app.
 * Handles both app launch URLs and URLs received while app is running.
 *
 * Deep links use a custom URL scheme: clipboardworks.workplace:///path?params
 * Example: clipboardworks.workplace:///login/verify?authUrl=...
 */
export function initializeDeepLinkHandler(onDeepLink: (url: string) => void): () => void {
  if (!Capacitor.isNativePlatform()) {
    return () => {
      // No-op for web
    };
  }

  let cleanup: (() => void) | undefined;

  void (async () => {
    // Check if app was launched via deep link
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      onDeepLink(normaliseDeepLink(String(launchUrl.url)));
    }

    // Listen for deep links while app is running
    const listener = await App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      onDeepLink(normaliseDeepLink(String(event.url)));
    });

    cleanup = () => {
      void listener.remove();
    };
  })();

  return () => {
    cleanup?.();
  };
}
